import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/rootReducer";
import { LoadingStatus } from "@/enums/LoadingStatus.enum";
import { ControlPoint } from "@/types/ControlPoint";
import { API_ENDPOINTS } from "@/api/endpoints.constant";
import { clivApiRequest } from "@/utils/apiClient";
import { systemConfig } from "@/config/systemConfig";
import { setControlPoints, setEntityStatus } from "@/store/slices/masterDataSlice";
import { masterDataIdbService } from "@/store/idbStorage/masterDataIdbService";
import type { MasterDataEntityKey } from "@/store/idbStorage/masterDataIdbService";
import * as Sentry from "@sentry/react";

const ENTITY_KEY: MasterDataEntityKey = "md_controlPoints";

export const useControlPoints = () => {
  const dispatch = useDispatch();
  const controlPoints = useAppSelector(
    (state: RootState) => state.masterData.controlPoints
  );
  const status = useAppSelector(
    (state: RootState) => state.masterData.entityStatus["controlPoints"] ?? LoadingStatus.IDLE
  );

  useEffect(() => {
    if (controlPoints?.length || status === LoadingStatus.LOADING) {
      return;
    }

    const load = async () => {
      dispatch(setEntityStatus({ entity: "controlPoints", status: LoadingStatus.LOADING }));
      try {
        const cached = await masterDataIdbService.getEntity<ControlPoint[]>(ENTITY_KEY);
        if (cached && cached.length) {
          dispatch(setControlPoints(cached));
          dispatch(setEntityStatus({ entity: "controlPoints", status: LoadingStatus.SUCCEEDED }));
          return;
        }
        const response = await clivApiRequest<ControlPoint[]>({
          url: `${systemConfig.apiUrl}/${API_ENDPOINTS.META.CONTROL_POINTS}`,
        });
        if (response.ok) {
          const data = response.data || [];
          dispatch(setControlPoints(data));
          await masterDataIdbService.setEntity(ENTITY_KEY, data);
          dispatch(setEntityStatus({ entity: "controlPoints", status: LoadingStatus.SUCCEEDED }));
        } else {
          dispatch(setEntityStatus({ entity: "controlPoints", status: LoadingStatus.FAILED }));
        }
      } catch (error) {
        Sentry.captureException(error as Error);
        dispatch(setEntityStatus({ entity: "controlPoints", status: LoadingStatus.FAILED }));
      }
    };

    void load();
  }, [controlPoints?.length, status, dispatch]);

  return { controlPoints, status };
};

